import { useState, useRef, useEffect, useMemo } from "react";

import api from "@helpers/api";

import { useCommunity } from "@contexts/CommunityContext";

import * as Mui from "@mui/material";

import CustomModal from "@components/CustomModal";

import { Input } from "@fragments";

import Popper from "@fragments/Popper";

import "./styles.scss";

import { ArrowDropDownRounded, AutoAwesomeRounded, KeyboardArrowRightRounded, LockRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import UserModal from "@components/Modals/UserModal";
import GiveUserAccessToSubscription from "@components/Modals/GiveUserAccessToSubscription";
import { UserAvatar } from "@components/Community";

/**
 * @deprecated Use `@modals/Member/MembersList` for the community-member viewer.
 * This modal is kept only for the user-picker flow in CommunityConfig/Split,
 * which still depends on the legacy `(communityId, callback)` open signature.
 */
export default function UsersModal({ modalRef }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const [members, setMembers] = useState([]);

  const { community } = useCommunity();

  const REF_config = useRef({});
  const REF_callback = useRef();

  const REF_userModal = useRef();
  const REF_rolePopper = useRef();
  const REF_giveAccessModal = useRef();

  const communityInfo = REF_config.current.community
    ? community.get(REF_config.current.community)
    : null;

  const isAdmin = communityInfo?.isAdmin?.();

  modalRef.current = {
    open(communityId, callback = () => { }) {
      REF_config.current = { community: communityId };
      REF_callback.current = callback;
      loadMembers();
      setOpen(true);
    },
  };

  async function loadMembers(search = "") {
    setLoading(true);

    try {
      const commId = REF_config.current?.community;

      let url = "community/member?limit=50";

      if (commId) {
        url += "&filters[community]=" + commId;
      }
      if (search) {
        url += "&filters[search]=" + search;
      }

      const { data } = await api.get(url);

      setMembers([...data]);
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }

  function handleMemberClick(user) {
    if (REF_callback.current) REF_callback.current(user);
    setOpen(false);
  }

  function SearchValue() {
    const [value, setValue] = useState("");
    const [debouncedValue, setDebouncedValue] = useState("");

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, 2000);

      return () => {
        clearTimeout(handler);
      };
    }, [value]);

    useEffect(() => {
      if (debouncedValue) {
        loadMembers(debouncedValue);
      }
    }, [debouncedValue]);

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        loadMembers(value);
      }
    };

    return (
      <Input
        className="mt-1 mb-2 search-member-in-modal"
        autoFocus={true}
        placeholder="Buscar membro da comunidade"
        value={value}
        onInput={(v) => {
          setValue(v);
        }}
        onKeyDown={handleKeyDown}
      />
    );
  }

  function SubscriptionInfo({ info }) {
    // Transforma subscription em array caso esteja como objeto com chaves numéricas
    const subscriptionArray = info?.subscription
      ? (Array.isArray(info.subscription)
        ? info.subscription
        : Object.values(info.subscription))
      : [];

    // Primeiro tenta encontrar uma assinatura ativa
    const activeSubs = subscriptionArray?.find(sub => sub.status === 'active');

    // Se não encontrar ativa, pega a última subscription (independente do status)
    const lastSubs = activeSubs || (subscriptionArray?.length > 0 ? subscriptionArray[subscriptionArray.length - 1] : null);

    function Badge() {
      return (
        <div className={`subs-badge subs-badge-${lastSubs.status}`}>
          <AutoAwesomeRounded fontSize="12px" style={{ width: '12px', height: '12px', marginRight: '6px', marginLeft: '3px', transform: 'translateY(0px)' }} />
          <span>
            {
              lastSubs.plan.label
            }{' - '}
            {
              {
                'active': 'Ativa',
                'inactive': 'Inativa',
                'canceled': 'Cancelada',
                'expired': 'Expirada',
                'unknown': 'Pendente'
              }[lastSubs.status] || 'Pendente'
            }
          </span>
        </div>
      )
    }

    return (
      <span className="member-subscription-info">
        {lastSubs ? <Badge /> : null}
      </span>
    );
  }

  function Member({ info }) {
    function handleOpenMemberPermissionModal(memberInfo, event) {
      event.stopPropagation();

      console.log('OPEN')

      const roleOptions = [
        {
          id: 'creator',
          label: 'Produtor',
          onClick: () => handleRoleChange(memberInfo, 'creator')
        },
        {
          id: 'member',
          label: 'Membro',
          onClick: () => handleRoleChange(memberInfo, 'member')
        }
      ];

      REF_rolePopper.current.open(roleOptions, "bottom", event.currentTarget);
    }

    async function handleRoleChange(memberInfo, newRole) {
      // Fecha o popper
      REF_rolePopper.current.close();

      // Verifica se o role selecionado é diferente do atual
      if (memberInfo.role === newRole) {
        return;
      }

      // Abre o modal de confirmação
      window.openConfirm({
        title: "Alterar Permissão",
        headline: "Deseja alterar a permissão do membro?",
        subtitle: `Alterar de "${getRoleLabel(memberInfo.role)}" para "${getRoleLabel(newRole)}"?`,
        buttons: [
          {
            text: "Cancelar",
            variant: "text",
            onClick: () => { }
          },
          {
            text: "Alterar permissão",
            variant: "contained",
            color: "primary",
            onClick: async () => {
              try {
                await api.patch("community/member/permission", {
                  user_id: memberInfo.id,
                  role: newRole,
                  community_id: REF_config.current?.community
                });

                window.setSnackbar("Permissão alterada com sucesso");
                loadMembers();
              } catch (error) {
                console.error("Erro ao alterar permissão:", error);
                window.setSnackbar("Erro ao alterar permissão");
              }
            }
          }
        ]
      });
    }

    function getRoleLabel(role) {
      const roleLabels = {
        'owner': 'Administrador',
        'creator': 'Produtor',
        'member': 'Membro'
      };
      return roleLabels[role] || role;
    }

    function handleEnableSubscription(event, user) {
      event.stopPropagation();
      const communityId = REF_config.current?.community;
      if (communityId && REF_giveAccessModal.current) {
        REF_giveAccessModal.current.open(user, communityId, loadMembers);
      }
    }

    const isOwner = info.role === 'owner'

    return (
      <div
        className="member-row anchor"
        onClick={() => handleMemberClick(info)}
      >
        <UserAvatar user={info} size={32} />

        <span className="member-name">
          {info.username}{" "}
          {info?.nickname && (
            <span style={{ opacity: 0.4 }}> @{info.nickname}</span>
          )}
        </span>


        {
          isAdmin ? (
            <>
              <SubscriptionInfo info={info} />

              {!['owner', 'admin', 'creator'].includes(info.role) && (
                <span className="member-enable-access pressable" onClick={(e) => handleEnableSubscription(e, info)}>
                  Ativar assinatura <ArrowDropDownRounded fontSize="12px" style={{ transform: 'translateY(2px)' }} />
                </span>
              )}

              <span className={"member-role " + (isOwner && 'disabled')} onClick={(e) => {
                if (info.role !== 'owner') {
                  handleOpenMemberPermissionModal(info, e)
                }
              }}>
                <small>{
                  {
                    'owner': 'Administrador',
                    'creator': "Produtor",
                    "member": "Membro"
                  }[info.role]}</small>
                {
                  isOwner
                    ? <LockRounded style={{ width: '14px', height: '14px' }} />
                    : <ArrowDropDownRounded fontSize="12px" />
                }
              </span>
            </>
          ) : <></>
        }


        <KeyboardArrowRightRounded className="member-open" />
      </div>
    );
  }
  function MemberLoading() {
    return (
      <div className="member-row" style={{ marginTop: "3px" }}>
        <Mui.Skeleton
          className="avatar"
          variant="circular"
          width={32}
          height={32}
        />

        <Mui.Skeleton
          variant="rectangular"
          width="100%"
          height={32}
          style={{ marginTop: "3px", borderRadius: "8px" }}
        />
      </div>
    );
  }

  const MEMO_Search = useMemo(() => <SearchValue />, [open]);

  return (
    <>
      <CustomModal
        maxWidth={750}
        bottomButtonCancelText="Concluir"
        loading={loading}
        title="Buscar membros"
        open={open}
        handleClose={() => setOpen(false)}
        bodyComponent={
          open ? (
            <div className="px-4 py-2">
              {MEMO_Search}

              {loading
                ? ["", "", "", "", ""].map((l, i) => <MemberLoading key={i} />)
                : members?.map((m, i) => (
                  <>
                    {i !== 0 && (
                      <Mui.Divider className="member-row-divider" />
                    )}
                    <Member key={i} info={m} />
                  </>
                ))}

              {!loading && !members.length && (
                <h4 className="center">Nenhum membro foi encontrado</h4>
              )}
            </div>
          ) : null
        }
      />
      <UserModal modalRef={REF_userModal} />
      <Popper controlRef={REF_rolePopper} />
      <GiveUserAccessToSubscription modalRef={REF_giveAccessModal} />
    </>
  );
}


